import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {isAxiosError} from 'axios'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {PorQue} from '@/components/ui/por-que'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {FieldError} from '@/components/field-error'
import {ListEmptyState} from '@/components/section-card'
import {UploadTrigger} from '@/components/upload-trigger'
import {
  argsDelReemplazo,
  porqueDelReemplazo,
  Decision,
  Documento,
  elDia,
  EmpresaContratada,
  EscribirAMano,
  ProcedenciaDeclarada,
} from '@/components/perfil-contratista-documento'
import {EVIDENCE_MAX_BYTES, evidenceTooBig} from '@/hooks/sgsst/evidencias/use-evidence'
import {
  useCargarPerfilEntregado,
  useConfirmarLineaPerfil,
  useConfirmarLineasPerfil,
  useContrastarPerfil,
  useCorregirLineaPerfil,
  usePerfilContratista,
  useQuitarLineaPerfil,
  type ContrastePerfilContratista,
  type PerfilContratista,
  type PerfilContratistaLinea,
  type Procedencia,
  type RequisitoDelPerfil,
} from '@/hooks/empresa/perfil_cargo/use-perfil-contratista'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

/** Sin rojo: D14 prohíbe el rojo automático. Lo no verificado va en neutro: no es una alarma. */
const TONOS = {
  verde: 'green',
  amarillo: 'amber',
  no_verificado: 'neutral',
  sin_perfil: 'neutral',
  sin_confirmar: 'neutral',
} as const

type Veredicto = keyof typeof TONOS

function Veredicto({valor, testId}: {valor: string; testId: string}) {
  const {t} = useTranslation()
  const veredicto = (valor in TONOS ? valor : 'no_verificado') as Veredicto
  return (
    <Tag tone={TONOS[veredicto]} data-testid={testId}>
      {t(`perfilContratista.veredicto.${veredicto}`)}
    </Tag>
  )
}

/** Los `data-testid` van por `linea.id` y no por texto: dos renglones iguales duplicarían testids
 * (strict mode de Playwright). */
function Linea({
  linea,
  canOperate,
  onConfirmar,
  onCorregir,
  onQuitar,
}: {
  linea: PerfilContratistaLinea
  canOperate: boolean
  onConfirmar: (lineaId: string) => void
  onCorregir: (lineaId: string, texto: string) => void
  onQuitar: (lineaId: string) => void
}) {
  const {t} = useTranslation()
  const [texto, setTexto] = useState<string | null>(null)
  const [invalido, setInvalido] = useState<string | null>(null)
  const [quitando, setQuitando] = useState(false)

  function guardar(evento: FormEvent) {
    evento.preventDefault()
    const fallo = validateRequired(texto ?? '')
    setInvalido(fallo)
    if (fallo) return
    onCorregir(linea.id, (texto ?? '').trim())
    setTexto(null)
  }

  return (
    <li className="space-y-1 border-t pt-2 first:border-t-0 first:pt-0" data-testid={`perfil-linea-${linea.id}`}>
      <p className="text-sm">{linea.texto}</p>
      {linea.fragmento && (
        <p className="text-xs text-muted-foreground">
          {t('perfilContratista.lineas.fragmento', {fragmento: linea.fragmento, pagina: linea.pagina ?? ''})}
        </p>
      )}
      {linea.confirmado && (
        <p className="text-xs text-muted-foreground">
          {t('perfilContratista.lineas.confirmada', {
            nombre: linea.confirmado_por_nombre ?? '',
            fecha: linea.confirmado_en ? elDia(linea.confirmado_en) : '',
          })}
        </p>
      )}
      {canOperate && texto === null && (
        <div className="flex flex-wrap gap-2">
          {!linea.confirmado && (
            <Button
              size="sm"
              variant="outline"
              data-testid={`perfil-linea-confirmar-${linea.id}`}
              onClick={() => onConfirmar(linea.id)}>
              {t('perfilContratista.lineas.confirmar')}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            data-testid={`perfil-linea-corregir-${linea.id}`}
            onClick={() => setTexto(linea.texto)}>
            {t('perfilContratista.lineas.corregir')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            data-testid={`perfil-linea-quitar-${linea.id}`}
            onClick={() => setQuitando(true)}>
            {t('perfilContratista.lineas.quitar')}
          </Button>
        </div>
      )}
      {texto !== null && (
        <form className="space-y-2" noValidate onSubmit={guardar}>
          <Input
            maxLength={tope('TEXTO')}
            value={texto}
            data-testid={`perfil-linea-texto-${linea.id}`}
            onBlur={() => setInvalido(validateRequired(texto ?? ''))}
            onChange={(evento) => setTexto(evento.target.value)}
          />
          <FieldError error={invalido ?? undefined} />
          <p className="text-xs text-muted-foreground">{t('perfilContratista.lineas.corregirAyuda')}</p>
          <Button size="sm" type="submit" data-testid={`perfil-linea-corregir-guardar-${linea.id}`}>
            {t('perfilContratista.lineas.corregirGuardar')}
          </Button>
          <Button size="sm" type="button" variant="ghost" onClick={() => setTexto(null)}>
            {t('common.cancel')}
          </Button>
        </form>
      )}
      <ConfirmDialog
        open={quitando}
        onOpenChange={setQuitando}
        title={t('perfilContratista.lineas.quitarTitulo')}
        description={t('perfilContratista.lineas.quitarCuerpo', {texto: linea.texto})}
        confirmLabel={t('perfilContratista.lineas.quitar')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          setQuitando(false)
          onQuitar(linea.id)
        }}
      />
    </li>
  )
}

function Contraste({
  contraste,
  canOperate,
  onContrastar,
}: {
  contraste: ContrastePerfilContratista
  canOperate: boolean
  onContrastar: (proyectoId: string) => void
}) {
  const {t} = useTranslation()
  return (
    <li
      className="space-y-2 border-t pt-3 first:border-t-0 first:pt-0"
      data-testid={`perfil-contraste-${contraste.proyecto_id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-medium">{contraste.proyecto_nombre}</p>
        <Veredicto valor={contraste.veredicto} testId={`perfil-contraste-veredicto-${contraste.proyecto_id}`} />
      </div>
      {!contraste.riesgos_verificables && (
        <p className="text-xs text-muted-foreground" data-testid="perfil-sin-riesgos">
          {t('perfilContratista.contraste.sinRiesgos')}
        </p>
      )}
      {!contraste.tareas_verificables && (
        <p className="text-xs text-muted-foreground" data-testid="perfil-sin-tareas">
          {t('perfilContratista.contraste.sinTareas')}
        </p>
      )}
      {contraste.diferencias.length > 0 && (
        <ul className="space-y-1">
          {contraste.diferencias.map((diferencia, indice) => (
            <li key={`${diferencia.tipo}-${indice}`} className="text-sm text-muted-foreground">
              {t(`perfilContratista.contraste.${claveDiferencia(diferencia.tipo, diferencia.nivel)}`, {
                texto: diferencia.texto,
                nivel: diferencia.nivel ?? '',
              })}
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        {contraste.corrida_en
          ? t('perfilContratista.contraste.corrida', {fecha: elDia(contraste.corrida_en)})
          : t('perfilContratista.contraste.sinConstancia')}
      </p>
      {canOperate && !contraste.asignacion_cerrada && (
        <>
          <Button
            size="sm"
            variant="outline"
            disabled={contraste.diferencias.length === 0}
            data-testid={`perfil-contrastar-${contraste.proyecto_id}`}
            onClick={() => onContrastar(contraste.proyecto_id)}>
            {t('perfilContratista.contraste.dejarConstancia')}
          </Button>
          {contraste.diferencias.length === 0 && (
            <p className="text-xs text-muted-foreground">{t('perfilContratista.contraste.nadaQueDejar')}</p>
          )}
        </>
      )}
    </li>
  )
}

/** Una fila de la Matriz sin valorar trae `nivel` null y tiene su propia frase. */
function claveDiferencia(tipo: string, nivel: string | null): string {
  return tipo === 'peligro_faltante' && !nivel ? 'peligro_faltante_sin_nivel' : tipo
}

/** Porqué de E10-H05 con copy propio: quien no tiene cargo no tiene fila en `requisito_cargo`. */
function Requisitos({requisitos}: {requisitos: RequisitoDelPerfil[]}) {
  const {t} = useTranslation()
  if (requisitos.length === 0) return null
  return (
    <div className="space-y-2" data-testid="perfil-contratista-requisitos">
      <p className="text-sm font-medium">{t('perfilContratista.requisitos.titulo')}</p>
      <p className="text-xs text-muted-foreground">{t('perfilContratista.requisitos.intro')}</p>
      <ul className="space-y-3">
        {requisitos.map((requisito) => (
          <li key={requisito.nombre} className="space-y-1" data-testid={`perfil-requisito-${requisito.nombre}`}>
            <p className="text-sm font-medium">{requisito.nombre}</p>
            <ol className="space-y-0.5 text-xs text-muted-foreground">
              {requisito.porque.map((renglon, indice) => (
                <li key={`${renglon.clave}-${indice}`}>
                  {t(`perfilContratista.porque.${renglon.clave}`, {
                    valor: renglon.valor,
                    articulo: requisito.norma_articulo ?? '',
                  })}
                </li>
              ))}
            </ol>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Perfil de quien no tiene cargo en el organigrama (E10-H09); los chequeos de ingreso leen esta hoja. */
export function PerfilContratistaSection({
  businessId,
  employeeId,
  canOperate,
  canFirmar,
  razonBloqueoFirma,
}: {
  businessId: string
  employeeId: string
  canOperate: boolean
  canFirmar: boolean
  razonBloqueoFirma: string | null
}) {
  const {t} = useTranslation()
  const perfil = usePerfilContratista(businessId, employeeId)
  const cargar = useCargarPerfilEntregado(businessId, employeeId)
  const confirmar = useConfirmarLineaPerfil(businessId, employeeId)
  const confirmarTodo = useConfirmarLineasPerfil(businessId, employeeId)
  const corregir = useCorregirLineaPerfil(businessId, employeeId)
  const quitar = useQuitarLineaPerfil(businessId, employeeId)
  const contrastar = useContrastarPerfil(businessId, employeeId)
  const [aMano, setAMano] = useState(false)
  const [cerrandoAMano, setCerrandoAMano] = useState(false)
  const [autor, setAutor] = useState('')
  const [fecha, setFecha] = useState('')
  const [pendiente, setPendiente] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (perfil.isLoading) return <Skeleton className="h-24 w-full" />
  // Un fallo de red no se pinta como vacío: afirmaría que el contratista no entregó perfil.
  if (perfil.isError || !perfil.data) {
    return <ErrorDeCarga error={perfil.error} />
  }
  const hoja = perfil.data
  const peligros = hoja.lineas.filter((linea) => linea.tipo === 'peligro')
  const funciones = hoja.lineas.filter((linea) => linea.tipo === 'funcion')
  const sinConfirmar = hoja.lineas.filter((linea) => !linea.confirmado).length
  const procedencia: Procedencia = {
    autorDeclarado: autor.trim() || null,
    fechaDeclarada: fecha || null,
    vistoCargadoEn: hoja.cargado_en ?? null,
  }

  function subir(archivo: File) {
    setPendiente(null)
    cargar.mutate({archivo, ...procedencia}, {onError: (err) => setError(translateApiError(err))})
  }

  return (
    <div className="space-y-5" data-testid="perfil-contratista">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* Lo decide el backend: contar aparte de la nómina tiene consecuencia legal. */}
          {hoja.cuenta_aparte && (
            <Tag tone="neutral" data-testid="perfil-contratista-cuenta-aparte">
              {t('perfilContratista.chipCuentaAparte')}
            </Tag>
          )}
          <PorQue
            label={t('perfilContratista.porqueLabel')}
            testId="perfil-contratista-porque"
            contenidoTestId="perfil-contratista-porque-note">
            <span className="block">{t('perfilContratista.intro')}</span>
            {hoja.cuenta_aparte && <span className="mt-1.5 block">{t('perfilContratista.cuentaAparte')}</span>}
          </PorQue>
        </div>
        <Veredicto valor={hoja.veredicto} testId="perfil-contratista-veredicto" />
      </div>

      <EmpresaContratada businessId={businessId} employeeId={employeeId} hoja={hoja} canOperate={canOperate} />

      {hoja.cargado_en !== null && <Documento hoja={hoja} />}

      {canOperate && (
        <div className="space-y-3">
          {!hoja.lector_disponible && (
            <p className="text-sm" data-testid="perfil-contratista-sin-lector">
              {t('perfilContratista.sinLector.aviso')}
            </p>
          )}
          <ProcedenciaDeclarada autor={autor} fecha={fecha} onAutor={setAutor} onFecha={setFecha} />
          <div className="flex flex-wrap items-center gap-3">
            <UploadTrigger
              label={t(hoja.lector_disponible ? 'perfilContratista.cargar' : 'perfilContratista.sinLector.cargar')}
              progress={cargar.isPending ? 100 : null}
              testId="perfil-contratista"
              onPick={(files) => {
                const archivo = files?.[0]
                setError(null)
                if (!archivo) return
                // El 422 por tamaño trae `detail` como lista y `translateApiError` cae al genérico.
                if (archivo.size > EVIDENCE_MAX_BYTES) return setError(evidenceTooBig(t, archivo))
                if (hoja.lineas.length > 0) return setPendiente(archivo)
                subir(archivo)
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              data-testid="perfil-contratista-a-mano-abrir"
              onClick={() => (aMano ? setCerrandoAMano(true) : setAMano(true))}>
              {t(aMano ? 'perfilContratista.cerrarAMano' : 'perfilContratista.escribirAMano')}
            </Button>
          </div>
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {aMano && canOperate && (
        <EscribirAMano
          businessId={businessId}
          employeeId={employeeId}
          hoja={hoja}
          procedencia={procedencia}
          onListo={() => setAMano(false)}
        />
      )}

      {hoja.lineas.length === 0 ? (
        <ListEmptyState
          title={t('perfilContratista.vacio')}
          body={canOperate ? t('perfilContratista.vacioAyuda') : t('perfilContratista.vacioSinPermiso')}
        />
      ) : (
        <div className="space-y-4">
          {canOperate && sinConfirmar > 0 && (
            <Button
              size="sm"
              data-testid="perfil-contratista-confirmar-todo"
              onClick={() => confirmarTodo.mutate(null)}
              disabled={confirmarTodo.isPending}>
              {t('perfilContratista.lineas.confirmarTodo', {n: sinConfirmar})}
            </Button>
          )}
          <ListaConfirmable
            titulo={t('perfilContratista.lineas.peligros')}
            lineas={peligros}
            canOperate={canOperate}
            testId="perfil-lista-peligros"
            onConfirmar={(lineaId) => confirmar.mutate(lineaId)}
            onCorregir={(lineaId, texto) => corregir.mutate({lineaId, texto})}
            onQuitar={(lineaId) => quitar.mutate(lineaId)}
          />
          <ListaConfirmable
            titulo={t('perfilContratista.lineas.funciones')}
            lineas={funciones}
            canOperate={canOperate}
            testId="perfil-lista-funciones"
            onConfirmar={(lineaId) => confirmar.mutate(lineaId)}
            onCorregir={(lineaId, texto) => corregir.mutate({lineaId, texto})}
            onQuitar={(lineaId) => quitar.mutate(lineaId)}
          />
        </div>
      )}

      {hoja.cargado_en !== null && (
        <Decision
          businessId={businessId}
          employeeId={employeeId}
          hoja={hoja}
          canFirmar={canFirmar}
          razonBloqueoFirma={razonBloqueoFirma}
        />
      )}

      <Contrastes hoja={hoja} canOperate={canOperate} onContrastar={(id) => contrastar.mutate(id)} />

      <Requisitos requisitos={hoja.requisitos} />

      <ConfirmDialog
        open={pendiente !== null}
        onOpenChange={(abierto) => !abierto && setPendiente(null)}
        title={t('perfilContratista.reemplazo.titulo')}
        description={t('perfilContratista.reemplazo.cuerpo', argsDelReemplazo(hoja))}
        porque={porqueDelReemplazo(t)}
        confirmLabel={t('perfilContratista.reemplazo.confirmar')}
        cancelLabel={t('common.cancel')}
        pending={cargar.isPending}
        onConfirm={() => pendiente && subir(pendiente)}
      />
      <ConfirmDialog
        open={cerrandoAMano}
        onOpenChange={setCerrandoAMano}
        title={t('perfilContratista.aMano.cerrarTitulo')}
        description={t('perfilContratista.aMano.cerrarCuerpo')}
        confirmLabel={t('perfilContratista.aMano.cerrarConfirmar')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          setCerrandoAMano(false)
          setAMano(false)
        }}
      />
    </div>
  )
}

/** 402 y 403 no se arreglan reintentando: el 402 lleva al upgrade. */
function ErrorDeCarga({error}: {error: unknown}) {
  const {t} = useTranslation()
  const status = isAxiosError(error) ? error.response?.status : undefined
  const clave =
    status === 402
      ? 'plans.upgradeRequired'
      : status === 403
        ? 'perfilContratista.sinPermiso'
        : 'perfilContratista.errorCarga'
  return (
    <p className="text-sm text-destructive" data-testid="perfil-contratista-error">
      {t(clave)}
    </p>
  )
}

/** Las obras anteriores siguen visibles: su constancia es lo que pide un inspector. */
function Contrastes({
  hoja,
  canOperate,
  onContrastar,
}: {
  hoja: PerfilContratista
  canOperate: boolean
  onContrastar: (proyectoId: string) => void
}) {
  const {t} = useTranslation()
  const hoy = hoja.contrastes.filter((contraste) => !contraste.asignacion_cerrada)
  const antes = hoja.contrastes.filter((contraste) => contraste.asignacion_cerrada)
  // Sin listas confirmadas, una lista vacía afirmaría «no está en ninguna obra».
  if (hoja.lineas.length === 0 || hoja.veredicto === 'sin_confirmar') return null
  return (
    <div className="space-y-2" data-testid="perfil-contratista-contrastes">
      <p className="text-sm font-medium">{t('perfilContratista.contraste.titulo')}</p>
      {hoy.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="perfil-contratista-sin-obras">
          {t('perfilContratista.contraste.sinObras')}
        </p>
      )}
      <ListaDeContrastes contrastes={hoy} canOperate={canOperate} onContrastar={onContrastar} />
      {antes.length > 0 && (
        <div className="space-y-2" data-testid="perfil-contratista-obras-anteriores">
          <p className="text-sm font-medium">{t('perfilContratista.contraste.anteriores')}</p>
          <ListaDeContrastes contrastes={antes} canOperate={canOperate} onContrastar={onContrastar} />
        </div>
      )}
    </div>
  )
}

function ListaDeContrastes({
  contrastes,
  canOperate,
  onContrastar,
}: {
  contrastes: ContrastePerfilContratista[]
  canOperate: boolean
  onContrastar: (proyectoId: string) => void
}) {
  return (
    <ul className="space-y-3">
      {contrastes.map((contraste) => (
        <Contraste
          key={contraste.proyecto_id}
          contraste={contraste}
          canOperate={canOperate}
          onContrastar={onContrastar}
        />
      ))}
    </ul>
  )
}

function ListaConfirmable({
  titulo,
  lineas,
  canOperate,
  testId,
  onConfirmar,
  onCorregir,
  onQuitar,
}: {
  titulo: string
  lineas: PerfilContratistaLinea[]
  canOperate: boolean
  testId: string
  onConfirmar: (lineaId: string) => void
  onCorregir: (lineaId: string, texto: string) => void
  onQuitar: (lineaId: string) => void
}) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <p className="text-sm font-medium">{titulo}</p>
      <ul className="space-y-2">
        {lineas.map((linea) => (
          <Linea
            key={linea.id}
            linea={linea}
            canOperate={canOperate}
            onConfirmar={onConfirmar}
            onCorregir={onCorregir}
            onQuitar={onQuitar}
          />
        ))}
      </ul>
    </div>
  )
}
